import { useState } from "react";
import { ChevronDown, ChevronRight, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { CriticIteration } from "@/hooks/use-proposals";

interface CriticHistoryPanelProps {
  iterations: CriticIteration[];
  finalScore: number;
}

// Etiquetas legibles para las 4 dimensiones del crítico (en orden y con su peso).
// El backend devuelve las claves en snake_case; aquí las traducimos para mostrar.
const DIMENSION_LABELS: Record<string, { label: string; weightLabel: string }> = {
  requisitos_tecnicos: { label: "Requisitos técnicos", weightLabel: "40%" },
  citacion_fuentes: { label: "Citación de fuentes", weightLabel: "30%" },
  estructura_formato: { label: "Estructura y formato", weightLabel: "20%" },
  tono_profesional: { label: "Tono profesional", weightLabel: "10%" },
};

const DIMENSION_ORDER = [
  "requisitos_tecnicos",
  "citacion_fuentes",
  "estructura_formato",
  "tono_profesional",
];

function scoreColor(score: number): string {
  if (score >= 0.8) return "text-green-700";
  if (score >= 0.6) return "text-yellow-700";
  return "text-red-700";
}

function scoreBgColor(score: number): string {
  if (score >= 0.8) return "bg-green-500";
  if (score >= 0.6) return "bg-yellow-500";
  return "bg-red-500";
}

function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

/**
 * Panel colapsable que muestra el historial del loop crítico-escritor para
 * una sección de propuesta. Sólo se renderiza si la sección tiene al menos
 * una iteración persistida en `extra_metadata`.
 *
 * Diseño:
 * - Header con score global (semáforo verde/amarillo/rojo) y nº de iteraciones.
 * - Body expandible con cada iteración: las 4 dimensiones (con barra), los
 *   gaps específicos por dimensión y el feedback consolidado.
 */
export function CriticHistoryPanel({ iterations, finalScore }: CriticHistoryPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!iterations || iterations.length === 0) {
    return null;
  }

  const Icon = finalScore >= 0.8 ? CheckCircle2 : AlertTriangle;
  const iconColor = finalScore >= 0.8 ? "text-green-600" : "text-yellow-600";

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 transition-colors text-left"
        aria-expanded={isExpanded}
        aria-controls="critic-history-body"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-900">
            Crítico de calidad
          </span>
          <span className={`text-sm font-semibold ${scoreColor(finalScore)}`}>
            <Icon className={`inline w-4 h-4 mr-1 ${iconColor}`} />
            {formatScore(finalScore)}
          </span>
          <span className="text-xs text-gray-500">
            ({iterations.length} {iterations.length === 1 ? "iteración" : "iteraciones"})
          </span>
        </div>
      </button>

      {isExpanded && (
        <div
          id="critic-history-body"
          className="px-4 pb-4 pt-1 space-y-4 border-t border-gray-200 bg-white"
        >
          {iterations.map((iter) => (
            <div
              key={iter.iteration}
              className="rounded-md border border-gray-200 p-3 space-y-3"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">
                  Iteración {iter.iteration}
                </h4>
                <span className={`text-sm font-bold ${scoreColor(iter.score)}`}>
                  {formatScore(iter.score)}
                </span>
              </div>

              <div className="space-y-2">
                {DIMENSION_ORDER.map((dimKey) => {
                  const dim = iter.dimensions?.[dimKey];
                  if (!dim) return null;
                  const meta = DIMENSION_LABELS[dimKey];
                  return (
                    <div key={dimKey}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-700">
                          {meta.label}{" "}
                          <span className="text-gray-400">({meta.weightLabel})</span>
                        </span>
                        <span className={`font-semibold ${scoreColor(dim.score)}`}>
                          {formatScore(dim.score)}
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${scoreBgColor(dim.score)} transition-all`}
                          style={{ width: `${Math.round(dim.score * 100)}%` }}
                        />
                      </div>
                      {dim.gaps && dim.gaps.length > 0 && (
                        <ul className="mt-1 ml-1 space-y-0.5">
                          {dim.gaps.map((gap, idx) => (
                            <li
                              key={idx}
                              className="text-xs text-gray-600 flex gap-1"
                            >
                              <span className="text-red-500" aria-hidden>•</span>
                              <span>{gap}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {iter.feedback && (
                <div className="border-t border-gray-100 pt-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Feedback aplicado al escritor:
                  </p>
                  <p className="text-xs text-gray-700 italic">{iter.feedback}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CriticHistoryPanel;
